import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSession, useContent } from '../hooks/useSession';
import { findLesson, findTrack, flatten, lessonKey } from '../lib/curriculum';
import { Blocks } from '../components/Blocks';
import { Tick } from '../components/Icon';
import { useConfirm } from '../components/ConfirmDialog';

export function LessonPage() {
  const { trackId = '', lessonId = '' } = useParams();
  const content = useContent();
  const { progress, toggle } = useSession();
  const { confirm, dialog } = useConfirm();
  const navigate = useNavigate();

  const track = findTrack(content, trackId);
  const lesson = findLesson(track, lessonId);

  if (!track || !lesson) {
    return (
      <div className="vu-wrap">
        <h1 className="vu-h2">Lesson not found</h1>
        <p className="vu-muted">
          It may have been renamed. <Link to="/" style={{ color: 'var(--text-link)' }}>Back to the dashboard</Link>.
        </p>
      </div>
    );
  }

  const key = lessonKey(track.id, lesson.id);
  const done = !!progress.done[key];

  // "Next" runs across track boundaries so a vendor can read the whole curriculum
  // start to finish without returning to the dashboard between tracks.
  const all = flatten(content.tracks);
  const index = all.findIndex((f) => f.track.id === track.id && f.lesson.id === lesson.id);
  const next = index >= 0 ? all[index + 1] : undefined;

  return (
    <div className="vu-wrap">
      <Link to={`/track/${track.id}`} className="vu-subtle" style={{ display: 'inline-block', marginBottom: 'var(--space-4)' }}>
        ← {track.name}
      </Link>

      <div className="vu-kicker">{track.name} · {lesson.mins} min read</div>
      <h1 className="vu-h1" style={{ fontSize: 'var(--fs-h2)', marginBottom: 'var(--space-2)' }}>{lesson.title}</h1>
      <p className="vu-lede" style={{ marginBottom: 'var(--space-8)' }}>{lesson.summary}</p>

      <Blocks blocks={lesson.blocks} />

      <div
        className="vu-noprint"
        style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-8)', paddingTop: 'var(--space-6)', borderTop: '1px solid var(--border-subtle)' }}
      >
        <button
          type="button"
          className="vu-btn"
          style={
            done
              ? { background: 'var(--success)', borderColor: 'var(--success)' }
              : { background: 'var(--brand-primary)' }
          }
          onClick={() => {
            void (async () => {
              // Progress is per-vendor and shown on the dashboard ring, so confirm
              // before changing it either way — the un-mark especially, since a
              // mis-tap there quietly undoes a completed lesson.
              const ok = await confirm(
                done
                  ? {
                      title: 'Mark this lesson as unread?',
                      body: `“${lesson.title}” goes back to unread and your completion drops.`,
                      confirmLabel: 'Mark as unread',
                    }
                  : {
                      title: 'Mark this lesson as read?',
                      body: `“${lesson.title}” counts towards your completion once you confirm.`,
                      confirmLabel: 'Mark as read',
                    },
              );
              if (ok) toggle('done', key, !done);
            })();
          }}
        >
          {done && <Tick size={14} />}
          {done ? 'Read — marked complete' : 'Mark as read'}
        </button>

        {next && (
          <button type="button" className="vu-btn vu-btn-ghost" onClick={() => navigate(`/track/${next.track.id}/${next.lesson.id}`)}>
            Next: {next.lesson.title} →
          </button>
        )}
      </div>

      {dialog}
    </div>
  );
}
